import React, { useRef, useEffect, useState, useMemo, act } from "react";
import { HexGrid, Layout, Hexagon, Text, GridGenerator } from "react-hexgrid";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { scaleLinear } from "d3-scale";
import { WidgetContainer } from "../shared/widgetContainer/widgetContainer";
import { Box, IconButton, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";

import { useTheme } from "@mui/material/styles";

// Color scale for [0..100]
const colorScale = scaleLinear().domain([0, 50, 100]).range(["#70C930", "#F9CA00", "#BA1650"]);

/**
 * Expects `data` to be an object like:
 * {
 *   all: [ { host, cpu, ram, disk, score }, ... ],
 *   dev: [ ... ],
 *   uat: [ ... ],
 *   ops: [ ... ],
 *   pro: [ ... ],
 *   other: [ ... ]
 * }
 */
const DataDogHexHeatmap = ({
  data,
  onHexClick = () => {},
  draggable = false,
  editMode = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef(null);

  // 1) Environment switching
  //    If `data` is undefined or empty, we default to an empty object.
  //    Then we extract the environment keys. E.g. ["all", "dev", "uat", ...].
  const envKeys = useMemo(() => (data ? Object.keys(data) : []), [data]);
  const [activeEnv, setActiveEnv] = useState((envKeys && envKeys[0]) || "all");

  // 2) The actual array of host objects for the active environment
  const safeData = useMemo(() => {
    if (!envKeys.length || !activeEnv || !data[activeEnv]) return [];
    return data[activeEnv];
  }, [data, envKeys, activeEnv]);

  // 3) State for sizing, panning, zooming, etc.
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });
  const [lastUpdatedDate, setLastUpdatedDate] = useState("");
  // For hover highlighting
  const [hoveredHex, setHoveredHex] = useState(null);
  const [hexWidth, setHexWidth] = useState(0);
  const [hexHeight, setHexHeight] = useState(0);
  // 4) Figure out how many columns/rows for the rectangular hex layout
  const cols = Math.ceil(Math.sqrt(safeData.length));
  const rows = Math.ceil(safeData.length / cols);

  // 5) Watch container size with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // 6) Auto zoom & center when data or container changes
  const calculateZoomAndOffset = () => {
    if (!safeData.length || !dimensions.width || !dimensions.height) return;

    let cols = Math.ceil(Math.sqrt(safeData.length));
    let rows = Math.ceil(safeData.length / cols);

    const newHexWidth = dimensions.width / cols / 14;
    const newHexHeight = dimensions.width / cols / 14;

    setHexWidth(newHexWidth);
    setHexHeight(newHexHeight);

    setOffset({ x: -(dimensions.width / 12), y: -(dimensions.height / 12) });
  };

  useEffect(() => {
    calculateZoomAndOffset();
  }, [safeData, dimensions]);

  useEffect(() => {
    calculateZoomAndOffset();
  }, [activeEnv]);

  // Force recalculation when data becomes available (loading -> loaded)
  useEffect(() => {
    if (data && safeData.length && dimensions.width) {
      setTimeout(() => {
        calculateZoomAndOffset();
      }, 50); // Small delay to ensure DOM is ready
    }
  }, [data, safeData.length, dimensions.width]);

  useEffect(() => {
    if (!data || lastUpdatedDate) return;

    const firstKey = Object.keys(data)[0];
    const timestamp = data[firstKey]?.[0]?.timestamp;

    if (timestamp) {
      const date = new Date(Number(timestamp)); // ✅ Convert epoch ms to Date
      const day = date.getDate();

      const ordinal = (day) => {
        if (day >= 11 && day <= 13) return `${day}th`;
        switch (day % 10) {
          case 1:
            return `${day}st`;
          case 2:
            return `${day}nd`;
          case 3:
            return `${day}rd`;
          default:
            return `${day}th`;
        }
      };

      const formatted = `${format(date, "h:mm a EEE")} ${ordinal(day)} of ${format(date, "MMMM")}`;
      setLastUpdatedDate(formatted);
    }
  }, [data, lastUpdatedDate, activeEnv]);

  // 7) Wheel zoom
  useEffect(() => {
    const handleWheel = (e) => {
      if (!containerRef.current?.contains(e.target)) return;
      e.preventDefault();
      setZoom((prev) => Math.min(5, Math.max(0.1, prev - e.deltaY * 0.0005)));
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);

  // 8) Panning
  const handleMouseDown = (e) => {
    setIsPanning(true);
    setLastPan({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsPanning(false);
  const handleMouseLeave = () => setIsPanning(false);

  // 9) Generate the Axial coordinates for a rectangular region
  const hexes = GridGenerator.rectangle(cols, rows).map(({ q, r, s }) => ({
    q,
    r,
    s,
  }));

  const colorPalette = ["#70C930", "#F9CA00", "#BA1650", "#E97F03", "#9D419B", "#DDA0DD"];

  const colors = envKeys.slice(1).reduce((acc, envKey, index) => {
    acc[envKey] = colorPalette[index % colorPalette.length];
    return acc;
  }, {});

  const averages = useMemo(() => {
    if (!safeData.length) return { cpu: 0, ram: 0, disk: 0 };

    const total = safeData.reduce(
      (acc, item) => {
        return {
          cpu: acc.cpu + (item.cpu || 0),
          ram: acc.ram + (item.ram || 0),
          disk: acc.disk + (item.disk || 0),
        };
      },
      { cpu: 0, ram: 0, disk: 0 }
    );

    const count = safeData.length;
    return {
      cpu: total.cpu / count,
      ram: total.ram / count,
      disk: total.disk / count,
    };
  }, [safeData]);

  // 10) Render
  return (
    <WidgetContainer
      loaded={true}
      download={false}
      maximize={true}
      editMode={editMode}
      loadingMessage="Loading live heatmap data..."
      draggable={draggable}
      updatedAt={lastUpdatedDate}
      ref={containerRef}
      title="Compute Heatmap"
      description={`${hexes.length} nodex in ${activeEnv.toUpperCase()} `}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          borderRadius: "12px",
          userSelect: "none",
          position: "relative",
          marginTop: "0px",
          marginBottom: "10px",
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.5,
            mb: 1.5,
            borderRadius: "6px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            minHeight: "28px",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: "500",
              textTransform: "uppercase",
              fontSize: "0.65rem",
              letterSpacing: 0.5,
            }}
          >
            Avg Usage
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {[
              { label: "CPU", value: averages.cpu },
              { label: "RAM", value: averages.ram },
              { label: "Disk", value: averages.disk },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.3,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    color: theme.palette.text.secondary,
                    fontWeight: "400",
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "600",
                    color: value < 50 ? "#70C930" : value < 80 ? "#F9CA00" : "#BA1650",
                    fontSize: "0.7rem",
                  }}
                >
                  {value.toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ borderRadius: "12px", overflow: "hidden" }}>
          {!data || !safeData.length ? (
            // Loading hexgrid skeleton
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: dimensions.height || 400,
                background:
                  "linear-gradient(45deg, rgba(255,255,255,0.02) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.02) 75%)",
                backgroundSize: "20px 20px",
                position: "relative",
                "& .loading-hex": {
                  animation: "colorPulse 3s ease-in-out infinite",
                  "@keyframes colorPulse": {
                    "0%": {
                      fill: "rgba(255,255,255,0.05)",
                      stroke: "rgba(255,255,255,0.1)",
                      transform: "scale(1)",
                    },
                    "33%": {
                      fill: "rgba(112, 201, 48, 0.3)", // Green
                      stroke: "rgba(112, 201, 48, 0.6)",
                      transform: "scale(1.05)",
                    },
                    "66%": {
                      fill: "rgba(249, 202, 0, 0.3)", // Yellow
                      stroke: "rgba(249, 202, 0, 0.6)",
                      transform: "scale(1.03)",
                    },
                    "85%": {
                      fill: "rgba(186, 22, 80, 0.3)", // Red
                      stroke: "rgba(186, 22, 80, 0.6)",
                      transform: "scale(1.07)",
                    },
                    "100%": {
                      fill: "rgba(255,255,255,0.05)",
                      stroke: "rgba(255,255,255,0.1)",
                      transform: "scale(1)",
                    },
                  },
                },
                "& .loading-hex-alt": {
                  animation: "colorWave 2.5s ease-in-out infinite",
                  "@keyframes colorWave": {
                    "0%": {
                      fill: "rgba(255,255,255,0.03)",
                      stroke: "rgba(255,255,255,0.08)",
                    },
                    "50%": {
                      fill: "rgba(157, 65, 155, 0.4)", // Purple
                      stroke: "rgba(157, 65, 155, 0.7)",
                    },
                    "100%": {
                      fill: "rgba(255,255,255,0.03)",
                      stroke: "rgba(255,255,255,0.08)",
                    },
                  },
                },
              }}
            >
              <svg width={dimensions.width || 800} height={dimensions.height || 400}>
                {/* Create a grid of loading hexagons that fills the space */}
                {(() => {
                  const containerWidth = dimensions.width || 800;
                  const containerHeight = dimensions.height || 400;

                  // Calculate how many hexagons fit in the space with proper spacing
                  const hexSize = Math.min(containerWidth / 30, containerHeight / 20, 20);
                  // For flat-top hexagons: horizontal spacing = 1.732 * radius, vertical spacing = 1.5 * radius
                  const xSpacing = hexSize * 1.732;
                  const ySpacing = hexSize * 1.5;

                  const cols = Math.floor(containerWidth / xSpacing) + 2;
                  const rows = Math.floor(containerHeight / ySpacing) + 2;

                  // Center the grid
                  const gridWidth = (cols - 1) * xSpacing;
                  const gridHeight = (rows - 1) * ySpacing;
                  const startX = (containerWidth - gridWidth) / 2;
                  const startY = (containerHeight - gridHeight) / 2;

                  return Array.from({ length: cols * rows }, (_, i) => {
                    const row = Math.floor(i / cols);
                    const col = i % cols;
                    const xOffset = (row % 2) * (xSpacing / 2);
                    const x = startX + col * xSpacing + xOffset;
                    const y = startY + row * ySpacing;

                    // Only render hexagons that are within bounds
                    if (
                      x < -hexSize ||
                      x > containerWidth + hexSize ||
                      y < -hexSize ||
                      y > containerHeight + hexSize
                    ) {
                      return null;
                    }

                    return (
                      <g key={i} transform={`translate(${x}, ${y})`}>
                        <polygon
                          className={Math.random() > 0.5 ? "loading-hex" : "loading-hex-alt"}
                          points={`0,${-hexSize} ${hexSize * 0.866},${-hexSize * 0.5} ${hexSize * 0.866},${hexSize * 0.5} 0,${hexSize} ${-hexSize * 0.866},${hexSize * 0.5} ${-hexSize * 0.866},${-hexSize * 0.5}`}
                          fill="rgba(255,255,255,0.05)"
                          stroke="rgba(255,255,255,0.1)"
                          strokeWidth="0.5"
                          style={{
                            animationDelay: `${Math.random() * 3}s`, // Random delay 0-3s
                            animationDuration: `${2 + Math.random() * 2}s`, // Random duration 2-4s
                          }}
                        />
                      </g>
                    );
                  }).filter(Boolean); // Remove null values
                })()}
              </svg>

              {/* Loading text overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: "calc(100vh / 8)",
                  textAlign: "center",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: "600",
                    fontSize: "1.2rem",
                    textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                    animation: "fadeInOut 2s ease-in-out infinite",
                    "@keyframes fadeInOut": {
                      "0%": { opacity: 0.6 },
                      "50%": { opacity: 1 },
                      "100%": { opacity: 0.6 },
                    },
                  }}
                >
                  Computing Heat Map
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.85rem",
                    mt: 0.5,
                    textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                    animation: "pulse 1.5s ease-in-out infinite",
                    animationDelay: "0.3s",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 0.4 },
                      "50%": { opacity: 0.8 },
                    },
                  }}
                >
                  Analyzing server metrics...
                </Typography>
              </Box>
            </Box>
          ) : (
            // Actual hexgrid
            <HexGrid width={dimensions.width} height={dimensions.height}>
              <g transform={`translate(${offset.x}, ${offset.y}) scale(${zoom})`}>
                <Layout
                  size={{ x: hexWidth, y: hexHeight }}
                  flat={false}
                  spacing={1}
                  origin={{ x: 0, y: 0 }}
                >
                  {hexes.map((hex, i) => {
                    const item = safeData[i] || {};
                    const score = typeof item.score === "number" ? item.score : 0;
                    const fillColor = colorScale(score);

                    const tooltipContent = `
                  <div style="
                    background-color: #1e1e1e;
                    color: #fff;
                    padding: 12px;
                    border-radius: 8px;
                    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
                    font-family: 'Segoe UI', sans-serif;
                    font-size: 12px;
                    width: 220px;
                    max-width: 220px;
                  ">
                    <div style="
                      font-weight: bold;
                      font-size: 13px;
                      color: #F9CA00;
                      margin-bottom: 8px;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                      white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
                    ">
                      ${item.host || "UNKNOWN HOST"} 
                    </div>
                
                    ${["CPU", "RAM", "DISK"]
                      .map((label) => {
                        const value = item[label.toLowerCase()];
                        const color =
                          label === "CPU" ? "#70C930" : label === "RAM" ? "#E97F03" : "#BA1650";
                        return `
                        <div style="margin-bottom: 6px;">
                          <div style="margin-bottom: 2px;"><strong style="color: ${color};">${label}</strong>: ${
                            value?.toFixed(1) ?? "N/A"
                          }%</div>
                          <div style="background-color: #333; border-radius: 4px; height: 10px; margin-bottom:10px; overflow: hidden;">
                            <div style="
                              width: ${value ?? 0}%;
                              height: 100%;
                              background-color: ${color};
                              transition: width 0.3s ease;
                            "></div>
                          </div>
                        </div>
                      `;
                      })
                      .join("")}
                
                  
                
                    <div style="
                      margin-top: 8px;
                      font-size: 10px;
                      color: #888;
                      border-top: 1px solid #333;
                      padding-top: 6px;
                    ">
                      <em>Score = weighted average of CPU, RAM, and Disk usage</em>
                    </div>
                  </div>
                `;

                    return (
                      <Hexagon
                        key={i}
                        q={hex.q}
                        r={hex.r}
                        s={hex.s}
                        onClick={() => onHexClick(item)}
                        data-tooltip-id={`tooltip-${i}`}
                        data-tooltip-html={tooltipContent}
                        onMouseEnter={() => setHoveredHex(i)}
                        onMouseLeave={() => setHoveredHex(null)}
                        onFocus={(e) => e.currentTarget.blur()} // blur on focus
                        style={{
                          fill: hoveredHex === i ? "#fff" : fillColor,
                          stroke: "#222",
                          strokeWidth: 0.5,
                          cursor: "pointer",
                          transition: "stroke 0.2s, stroke-width 0.2s",
                        }}
                      />
                    );
                  })}
                </Layout>
              </g>
            </HexGrid>
          )}
        </Box>
        {/* Render a tooltip for each hex cell */}
        {hexes.map((_, i) => (
          <ReactTooltip key={i} id={`tooltip-${i}`} />
        ))}
      </div>
      {/* Buttons to switch environment */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        {envKeys.map((envKey) => (
          <IconButton
            key={envKey}
            size="small"
            onClick={() => setActiveEnv(envKey)}
            sx={{
              borderRadius: 2,
              fontSize: 10,
              fontWeight: "bold",

              backgroundColor:
                activeEnv === envKey ? (envKey === "all" ? "#ccc" : colors[envKey]) : "transparent",
              color:
                activeEnv === envKey
                  ? envKey === "all"
                    ? "#000"
                    : theme.palette.text.invert
                  : theme.palette.text.primary,

              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              "&:hover": {
                backgroundColor: activeEnv === envKey ? colors[envKey] : "rgba(255, 255, 255, 0.2)",
                boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.3)",
              },
            }}
          >
            {envKey.toUpperCase()}
          </IconButton>
        ))}
      </Box>
    </WidgetContainer>
  );
};

export default DataDogHexHeatmap;
